import { useState, useEffect, useRef } from 'react';
import { playUserAudioPromise } from '../../utils/playUserAudioPromise';
import styles from './Game.module.css';

const getDangerZoneHeight = (theme) => {
  if (theme === 'water') {
    return Math.max(320, Math.min(Math.round(window.innerHeight * 0.48), 440));
  }

  return 256;
};

const FallingWord = ({
  text,
  audio,
  translation,
  tips,
  isProcessing,
  speed,
  correctAnswer,
  showTranslation,
  dispatch,
  theme = 'fire',
  currentSentenceIndex = 0,
  onProcessingStart = () => {},
  onTranslationComplete = () => {}
}) => {
  const [position, setPosition] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isRapidFalling, setIsRapidFalling] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const animationRef = useRef(null);
  const wordRef = useRef(null);
  const lastUpdateTimeRef = useRef(Date.now());
  const prevSentenceIndexRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const countdownStartTimeRef = useRef(null);
  const translationRunKeyRef = useRef(null);

  useEffect(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    countdownStartTimeRef.current = Date.now();

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [currentSentenceIndex]);

  useEffect(() => {
    if (isProcessing || correctAnswer !== null) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [isProcessing, correctAnswer]);

  useEffect(() => {
    const isNewSentence = currentSentenceIndex !== prevSentenceIndexRef.current;

    if (isNewSentence) {
      prevSentenceIndexRef.current = currentSentenceIndex;
      setPosition(7);
      setVisible(true);
      setIsRapidFalling(false);
      setIsFading(false);
      onProcessingStart(false);

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    }

    lastUpdateTimeRef.current = Date.now();

    if (!isRapidFalling && (isProcessing || correctAnswer !== null)) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const animate = () => {
      const now = Date.now();
      const deltaTime = (now - lastUpdateTimeRef.current) / 1000;
      lastUpdateTimeRef.current = now;

      const currentSpeed = isRapidFalling ? speed * 20 : speed;

      setPosition((prev) => {
        if (isProcessing) {
          return prev;
        }

        const increment = currentSpeed * deltaTime * 60;
        const newPos = prev + increment;
        const screenHeight = window.innerHeight;
        const fromBottom = screenHeight - (screenHeight * newPos / 100);
        const dangerZoneHeight = getDangerZoneHeight(theme);

        if (fromBottom <= dangerZoneHeight && !isProcessing && correctAnswer === null) {
          dispatch({ type: 'SET_CORRECT_ANSWER', payload: false });
        }

        return newPos;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [
    currentSentenceIndex,
    isProcessing,
    correctAnswer,
    isRapidFalling,
    speed,
    theme,
    dispatch,
    onProcessingStart
  ]);

  useEffect(() => {
    if (!showTranslation) {
      translationRunKeyRef.current = null;
      return;
    }

    const runKey = `${currentSentenceIndex}:${correctAnswer}:${audio || ''}`;
    if (translationRunKeyRef.current === runKey) {
      return;
    }
    translationRunKeyRef.current = runKey;

    let cancelled = false;

    const run = async () => {
      try {
        await playUserAudioPromise(audio);
        if (cancelled) return;

        if (correctAnswer === true) {
          setIsFading(true);
          setVisible(false);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          if (cancelled) return;
        } else {
          setIsRapidFalling(true);
          await new Promise((resolve) => setTimeout(resolve, 1500));
          if (cancelled) return;
        }

        onTranslationComplete();
      } catch (err) {
        console.error('Audio playback failed:', err);
        if (cancelled) return;
        onTranslationComplete();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [showTranslation, currentSentenceIndex, audio, correctAnswer, onTranslationComplete]);

  useEffect(() => {
    onProcessingStart(showTranslation || isProcessing || correctAnswer !== null);
  }, [showTranslation, isProcessing, correctAnswer, onProcessingStart]);

  if (!visible) {
    return null;
  }

  return (
    <div
      ref={wordRef}
      className={`${styles.fallingWord} ${
        theme === 'water' ? styles.fallingWordWater : styles.fallingWordFire
      } ${isFading ? styles.fadeOut : ''}`}
      style={{
        top: `${position}%`,
        transition: isFading ? 'opacity 2s ease-out' : 'none'
      }}
    >
      <div className={styles.fallingWordContent}>
        {showTranslation ? (
          <p className={styles.wordPrimaryText}>
            {correctAnswer === true ? `✓ ${translation}` : `✕ ${translation}`}
          </p>
        ) : (
          <>
            <p className={styles.wordPrimaryText}>{text}</p>
            {tips && <div className={styles.tips}>{tips}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default FallingWord;
